import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type RevealVariant = 'rise' | 'media' | 'fade' | 'line'

type RevealState = 'idle' | 'hidden' | 'shown'

interface RevealProps {
    children?: ReactNode
    as?: ElementType
    className?: string
    delay?: number
    variant?: RevealVariant
    id?: string
}

type Listener = (entry: IntersectionObserverEntry) => void

/*
 * One IntersectionObserver is shared by every Reveal on the page, so a
 * long page costs one observer instead of dozens. Its first report says
 * where each element starts without forcing a layout read; elements that
 * start below the fold are hidden off screen, then shown once when they
 * approach the viewport and never animated again.
 */
const listeners = new Map<Element, Listener>()
let observer: IntersectionObserver | null = null

const getObserver = (): IntersectionObserver => {
    if (!observer) {
        observer = new IntersectionObserver((entries) => {
            for (const entry of entries) listeners.get(entry.target)?.(entry)
        }, { rootMargin: '0px 0px -6% 0px', threshold: 0 })
    }

    return observer
}

const watch = (element: Element, listener: Listener) => {
    listeners.set(element, listener)
    getObserver().observe(element)
}

const unwatch = (element: Element) => {
    listeners.delete(element)
    observer?.unobserve(element)
}

const Reveal: FC<RevealProps> = ({ children, as: Tag = 'div', className, delay = 0, variant = 'rise', id }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const [state, setState] = useState<RevealState>('idle')

    useEffect(() => {
        const element = ref.current

        if (!element) return
        if (!('IntersectionObserver' in window)) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let first = true

        watch(element, (entry) => {
            if (first) {
                first = false

                // Already on screen at load: leave it exactly as rendered.
                if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
                    unwatch(element)
                    return
                }

                setState('hidden')
                return
            }

            if (entry.isIntersecting) {
                setState('shown')
                unwatch(element)
            }
        })

        return () => unwatch(element)
    }, [])

    const style = delay ? ({ '--reveal-delay': `${delay}ms` } as CSSProperties) : undefined

    return (
        <Tag
            ref={ref}
            id={id}
            data-reveal={state}
            data-variant={variant}
            className={cn('reveal', className)}
            style={style}
        >
            {children}
        </Tag>
    )
}

export default Reveal
